import { useState } from 'react';
import NextLink from 'next/link';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSnackbar } from 'notistack';
import { useRouter } from 'next/router';
import Page from '../../components/Page';
import { PATH_AUTH } from '../../routes/paths';
import { ResetPasswordForm } from '../../sections/auth/reset-password';
import useIsMountedRef from '../../hooks/useIsMountedRef';
import axios from '../../utils/axios';
import AuthLayout from '../../sections/auth/AuthLayout';
import { BotaoAuth, CampoAuth, ENTRADA_AUTH, LinkVoltar, SenhaAuth } from '../../sections/auth/CamposAuth';

/* ============================================================================
   Recuperar senha - visual novo.

   Duas etapas na mesma rota, como antes: pedir o codigo (ResetPasswordForm) e,
   depois de enviado, informar codigo + nova senha (`POST /login/verificacao`).
   O contrato com a API nao mudou em nada.

   A folha de `!important` que estilizava os campos do MUI por dentro foi removida
   junto com o MUI - ver o comentario em sections/auth/CamposAuth.jsx.

   DEFEITO CORRIGIDO na validacao: `senhaConfirmacao` era
   `Yup.string().oneOf([...])` SEM `.required()`. Deixar o campo de confirmacao
   VAZIO passava na validacao (undefined nao entra na checagem do oneOf) e a senha
   era trocada sem ninguem confirmar nada - o campo existia sem cumprir a funcao
   que justifica existir.
   ============================================================================ */

ResetPassword.getLayout = function getLayout(page) {
  return page;
};

export default function ResetPassword() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const { push } = useRouter();
  const { enqueueSnackbar } = useSnackbar();
  const isMountedRef = useIsMountedRef();

  const Schema = Yup.object().shape({
    email: Yup.string().email('E-mail invalido').required('Email e obrigatorio'),
    codigoConfirmacao: Yup.string().required('Token e obrigatorio'),
    senha: Yup.string().required('Senha e obrigatoria'),
    senhaConfirmacao: Yup.string()
      .required('Confirme a nova senha')
      .oneOf([Yup.ref('senha'), null], 'Senhas nao coincidem'),
  });

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(Schema),
    defaultValues: { email: '', senha: '', senhaConfirmacao: '', codigoConfirmacao: '' },
  });

  const onSubmit = async (data) => {
    try {
      const response = await axios.post(`/login/verificacao`, {
        email: data.email,
        senha: data.senha,
        codigoConfirmacao: data.codigoConfirmacao,
      });

      if ((response.status === 200 || response.status === 201) && isMountedRef.current) {
        enqueueSnackbar('Senha alterada com sucesso.', { variant: 'success' });
        reset();
        push(PATH_AUTH.login);
      }
    } catch (err) {
      const error = err?.response?.data;
      if (error?.errors?.length) enqueueSnackbar(error.errors[0].message, { variant: 'error' });
      else if (error?.message) enqueueSnackbar(error.message, { variant: 'error' });
      else enqueueSnackbar('Ops, ocorreu um erro no sistema.', { variant: 'error' });
    }
  };

  const headerContent = !sent ? (
    <div className="mb-6">
      <div className="mb-4 flex h-[52px] w-[52px] items-center justify-center rounded-[14px] border border-[rgba(30,136,229,0.15)] bg-gradient-to-br from-[rgba(30,136,229,0.12)] to-[rgba(56,189,248,0.08)]">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
          <rect x="2" y="4" width="20" height="16" rx="3" stroke="#1E88E5" strokeWidth="1.5" />
          <path d="M2 7l10 7 10-7" stroke="#1E88E5" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </div>
      <h1 className="m-0 text-[24px] font-extrabold tracking-[-0.4px] text-ink">Recuperar senha</h1>
      <p className="mb-0 mt-2 text-[12.5px] leading-[1.55] text-muted-foreground">
        Informe seu e-mail e enviaremos um código de verificação
      </p>
    </div>
  ) : (
    <div className="mb-6">
      <div className="mb-4 flex h-[52px] w-[52px] items-center justify-center rounded-[14px] border border-[rgba(74,222,128,0.2)] bg-gradient-to-br from-[rgba(74,222,128,0.12)] to-[rgba(34,197,94,0.08)]">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
          <circle cx="12" cy="12" r="10" stroke="#22C55E" strokeWidth="1.5" />
          <path d="M8 12l3 3 5-5" stroke="#22C55E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <h1 className="m-0 text-[24px] font-extrabold tracking-[-0.4px] text-ink">Verifique seu e-mail</h1>
      <p className="mb-0 mt-2 text-[12.5px] leading-[1.55] text-muted-foreground">
        Enviamos um código para <strong className="text-primary">{email}</strong>. Insira abaixo para redefinir sua
        senha.
      </p>
    </div>
  );

  const footer = (
    <>
      <LinkVoltar href={PATH_AUTH.login} Componente={NextLink} />
    </>
  );

  return (
    <Page title="Recuperar senha - GPShub">
      <AuthLayout headerContent={headerContent} footer={footer} maxFormWidth={510}>
        {!sent ? (
          <ResetPasswordForm
            onSent={() => setSent(true)}
            onGetEmail={(value) => {
              setEmail(value);
              setValue('email', value);
            }}
          />
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-3.5" noValidate>
            <CampoAuth id="rp-email" rotulo="E-mail" erro={errors.email?.message}>
              <input id="rp-email" className={ENTRADA_AUTH} disabled {...register('email')} />
            </CampoAuth>

            <CampoAuth
              id="rp-codigo"
              rotulo="Código de verificação"
              erro={errors.codigoConfirmacao?.message}
              dica="É o código que chegou no e-mail acima."
            >
              <input
                id="rp-codigo"
                className={ENTRADA_AUTH}
                autoComplete="one-time-code"
                inputMode="numeric"
                {...register('codigoConfirmacao')}
              />
            </CampoAuth>

            <SenhaAuth
              id="rp-senha"
              rotulo="Nova senha"
              erro={errors.senha?.message}
              registro={register('senha')}
            />

            <SenhaAuth
              id="rp-senha-confirma"
              rotulo="Confirmar nova senha"
              erro={errors.senhaConfirmacao?.message}
              registro={register('senhaConfirmacao')}
            />

            <BotaoAuth carregando={isSubmitting} rotuloCarregando="Redefinindo...">
              Redefinir senha
            </BotaoAuth>
          </form>
        )}
      </AuthLayout>
    </Page>
  );
}
